import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import { LocalizationObject } from '../redux/slices/userSlice'

// Extend dayjs with the necessary plugins
dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(timezone)

//use this for iso date time
export const ISO_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ'
export const ISO_DATE_TIME_FORMAT_Z = 'YYYY-MM-DDTHH:mm:ss[Z]'
// default localization preferences
export const defaultLocalization: LocalizationObject = {
    dateFormat: 'MM/DD/YYYY',
    timeFormat: 'HH:mm:ss',
    timeZone: null,
    language: '',
}

// date format options
export const DATE_FORMAT_OPTIONS: string[] = ['DD/MM/YYYY', 'MM/DD/YYYY']

// time format options
export const TIME_FORMAT_OPTIONS: string[] = ['HH:mm', 'hh:mm A']

const isValidTimezone = (tz?: string): boolean => {
    if (!tz) return false
    try {
        Intl.DateTimeFormat(undefined, { timeZone: tz })
        return true
    } catch {
        return false
    }
}

export const getDateTimeSeconds = (date: dayjs.ConfigType): string => {
    const convertedDatec = dayjs(date).format('MM/DD/YYYY HH:mm:ss')

    return convertedDatec
}

export const getDateTime = (date: dayjs.ConfigType): string => {
    const convertedDatec = dayjs(date).format('MM/DD/YYYY HH:mm')

    return convertedDatec
}
export const getDate = (date: dayjs.ConfigType): string => {
    const convertedDatec = dayjs(date).format('MM/DD/YYYY')

    return convertedDatec
}

export const getDateInISOFormate = (date: dayjs.ConfigType): string => {
    const convertedDatec = dayjs(date).format(ISO_DATE_TIME_FORMAT_Z)
    return convertedDatec
}

export const getDateTimeInISOFormate = (
    date: dayjs.ConfigType,
    timezone: string | null | undefined,
    format?: string | null | undefined,
): string => {
    if (!date || date === 'Invalid Date') return ''
    const zone = timezone || dayjs.tz.guess()
    let parsed: dayjs.Dayjs
    if (format) {
        parsed = dayjs.tz(date, format, zone)
    } else {
        parsed = dayjs.tz(date, zone)
    }
    if (!parsed.isValid()) {
        return ''
    }
    return parsed.utc().format(ISO_DATE_TIME_FORMAT)
}

export const getDateTimeTZFormate = (date: dayjs.ConfigType): string => {
    const convertedDatec = dayjs(date).format()
    return convertedDatec
}

export const getDateTimeUserFormate = (
    date: dayjs.ConfigType,
    format: string,
    timezone: string,
): string => {
    const finalTz = isValidTimezone(timezone) ? timezone : dayjs.tz.guess()
    return dayjs.utc(date).tz(finalTz).format(format)
}

export const getDateYYYYMMDDFormate = (date: dayjs.ConfigType): string => {
    const convertedDatec = dayjs(date).format('YYYY-MM-DD')

    return convertedDatec
}

export const isDate1BiggerThanDate2 = (
    date1: string,
    date2: string,
): boolean => {
    const date1Obj = dayjs(date1)
    const date2Obj = dayjs(date2)
    return date1Obj.isAfter(date2Obj)
}

export const getMaximumDate = (validDates: string[]): string => {
    return validDates.reduce(
        (max, current) =>
            isDate1BiggerThanDate2(current, max) ? current : max,
        validDates[0],
    )
}

export const getMinimumDate = (validDates: string[]): string => {
    return validDates.reduce(
        (min, current) =>
            isDate1BiggerThanDate2(min, current) ? current : min,
        validDates[0],
    )
}

// user preferred date format
export const getUserPreferredDateFormat = (
    date: dayjs.ConfigType,
    format: string,
): string => {
    const convertedDate = dayjs(date).format(format)
    return convertedDate
}

// user preferred time format
export const getUserPreferredTimeFormat = (
    date: dayjs.ConfigType,
    format: string,
): string => {
    const convertedDate = dayjs(date).format(format + ' HH:mm')
    return convertedDate
}

// user preferred dateTime format
export const getUserPreferredDateTimeFormat = (
    date: dayjs.ConfigType,
    dateFormat: string,
    timeFormat?: string,
): string => {
    const convertedDate = timeFormat
        ? dayjs(date).format(dateFormat + ' ' + timeFormat)
        : dayjs(date).format(dateFormat)
    return convertedDate
}

// user preferred localization format
export const getUserPreferredLocalizationFormat = (
    date: dayjs.ConfigType,
    dateFormat: string,
    timeFormat?: string,
    timeZone?: string,
): string => {
    const finalTz = isValidTimezone(timeZone ?? undefined)
        ? timeZone ?? undefined
        : dayjs.tz.guess()
    const formatString = timeFormat ? `${dateFormat} ${timeFormat}` : dateFormat
    return dayjs(date).tz(finalTz).format(formatString)
}

// effective localization settings
export const getEffectiveLocalization = (
    userLocalization?: Partial<LocalizationObject>,
): LocalizationObject => {
    return {
        dateFormat:
            userLocalization?.dateFormat || defaultLocalization.dateFormat,
        timeFormat:
            userLocalization?.timeFormat || defaultLocalization.timeFormat,
        timeZone: userLocalization?.timeZone || defaultLocalization.timeZone,
        language: userLocalization?.language || defaultLocalization.language,
    }
}

// user preferred localization format (only date) with user preferences
export const formatDateWithUserPreferences = (
    date: dayjs.ConfigType,
    userLocalization?: Partial<LocalizationObject>,
): string => {
    const { dateFormat, timeZone } = getEffectiveLocalization(userLocalization)
    const finalTz = isValidTimezone(timeZone ?? undefined)
        ? timeZone ?? undefined
        : dayjs.tz.guess()
    return dayjs(date).tz(finalTz).format(dateFormat)
}

// user preferred localization format with user preferences
export const formatDateTimeWithUserPreferences = (
    date: dayjs.ConfigType,
    userLocalization?: Partial<LocalizationObject>,
    inputFormat?: string,
): string => {
    const { dateFormat, timeFormat, timeZone } =
        getEffectiveLocalization(userLocalization)
    const finalTz = isValidTimezone(timeZone ?? undefined)
        ? timeZone ?? undefined
        : dayjs.tz.guess()
    const formatString = timeFormat ? `${dateFormat} ${timeFormat}` : dateFormat
    const parsed = inputFormat ? dayjs(date, inputFormat) : dayjs(date)
    return parsed.tz(finalTz).format(formatString)
}

// convert and format the date for Maritime data
export const convertAndFormatDateForMaritime = (
    date: dayjs.ConfigType,
    userLocalization?: Partial<LocalizationObject>,
): string => {
    if (!date || date === 'Invalid Date') return ''
    const { dateFormat, timeFormat, timeZone } =
        getEffectiveLocalization(userLocalization)
    const finalTz = isValidTimezone(timeZone ?? undefined)
        ? timeZone ?? undefined
        : dayjs.tz.guess()
    const formatString = timeFormat ? `${dateFormat} ${timeFormat}` : dateFormat
    const parsed = dayjs.utc(date).tz(finalTz)
    if (!parsed.isValid()) return ''
    return parsed.format(formatString)
}

/**
 * Convert a date from one timezone to another.
 * @param date - The date to convert.
 * @param currentTimezone - The timezone of the input date.
 * @param targetTimezone - The timezone to convert the date to.
 * @returns The converted date in the target timezone.
 */
export const convertDateToTargetTimezone = (
    date: dayjs.ConfigType,
    currentTimezone: string,
    targetTimezone: string,
): string => {
    if (!isValidTimezone(currentTimezone) || !isValidTimezone(targetTimezone)) {
        throw new Error('Invalid timezone provided')
    }
    return dayjs(date)
        .tz(currentTimezone)
        .tz(targetTimezone)
        .format(ISO_DATE_TIME_FORMAT)
}
